import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { HaggleStoredFilters } from '@/components/filters/HaggleStoredFilters';
import LoadingSkeletonProductList from '@/components/product/LoadingSkeletonProductList';
import ProductList from '@/components/product/ProductList';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { ICollectionProduct } from '@/interfaces/api/product/ICollectionProduct';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const AdminProductsDashboard = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchParams] = useSearchParams();

	const {
		data: products,
		isLoading: productsIsLoading,
		isError: productIsError,
		error: productError,
	} = useQuery<ICollectionProduct>({
		queryKey: [
			ApiQueryKeys.getAllHaggleProducts,
			pageSize,
			pageNumber,
			searchParams.toString(),
		],
		queryFn: () =>
			haggleApiService.getAllHaggleProducts({
				params: {
					'page[size]': pageSize,
					'page[number]': pageNumber,
					'filterQuery[vendor]': searchParams.getAll('vendor'),
					'filterQuery[productType]': searchParams.getAll('type'),
					'filterQuery[countryName]': searchParams.getAll('country'),
					'filterQuery[minPrice]': searchParams.get('minPrice')
						? Number(searchParams.get('minPrice'))
						: undefined,
					'filterQuery[maxPrice]': searchParams.get('maxPrice')
						? Number(searchParams.get('maxPrice'))
						: undefined,
					'filterQuery[status]': searchParams.get('status') || undefined,
				},
			}),
		refetchOnWindowFocus: false,
	});

	if (productIsError) {
		if (productError instanceof Error) {
			notificationService.error(productError.message);
		} else {
			notificationService.error('Unknown error fetching products');
		}
	}

	const handlePaginationOptions = (
		collection: ICollectionProduct,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	return (
		<div className="flex flex-col">
			<div className="mb-2">
				<HaggleStoredFilters />
			</div>
			<div className="flex-1">
				{productsIsLoading && <LoadingSkeletonProductList />}
				{products && !productsIsLoading && (
					<ProductList
						collectionProduct={products}
						paginationOption={handlePaginationOptions(products)}
					/>
				)}
			</div>
		</div>
	);
};

export default AdminProductsDashboard;
